import React from 'react';
import PropTypes from 'prop-types';

import {Box} from 'views/components';
import {
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
} from 'views/icons';

import moment from 'moment-timezone';
import {Translate} from 'views/module';

const ScheduleCost = (props) => {
  const {rangeView, view, events, clientPaymentPlan} = props;

  const rawHourlyRate = clientPaymentPlan?.hourlyRate;
  const hourlyRate = (typeof rawHourlyRate === 'number' && !isNaN(rawHourlyRate)) ? rawHourlyRate :
    (typeof rawHourlyRate === 'string' && !isNaN(parseFloat(rawHourlyRate))) ? parseFloat(rawHourlyRate) :
    null;

  const numberOfHours = React.useMemo(() => {
    let start = null;
    let end = null;
    if (rangeView) {
      if (view === 'month') {
        // Find the month strictly between start and end
        const startDate = moment(rangeView.start);
        const endDate = moment(rangeView.end);

        // How many whole months between start and end?
        const monthDiff = endDate.diff(startDate, 'months');

        // If there is a month strictly between and it doesn't start on day 1
        if (monthDiff >= 1 && startDate.date() !== 1) {
          const nextMonth = moment(startDate).startOf('month').add(1, 'month');

          start = moment(nextMonth).startOf('month');
          end = moment(nextMonth).endOf('month');
        } else {
          // If both dates are in the same month or no month strictly between, use startDate's month
          start = moment(startDate).startOf('month');
          end = moment(startDate).endOf('month');
        }
      } else {
        start = moment(rangeView[0]);
        end = moment(rangeView[rangeView.length - 1]).endOf('day');
      }
    } else {
      if (view === 'month') {
        start = moment().startOf('month');
        end = moment().endOf('month');
      } else {
        start = moment().startOf('week');
        end = moment().endOf('week');
      }
    }

    // Filter events by start and end
    const filteredEvents = Object.values(events)
        .filter((event) => {
          const eventStart = moment.tz(event.eventData.startTime, event.eventData.timezone);
          if (!start || !end) return false;
          return eventStart.isSameOrAfter(start) && eventStart.isSameOrBefore(end);
        });

    // Calculate total hours
    const totalHours = filteredEvents.reduce((sum, event) => {
      const startTime = moment.tz(event.eventData.startTime, event.eventData.timezone);
      const endTime = moment.tz(event.eventData.endTime, event.eventData.timezone);
      let breakDuration = 0;
      if (event.type === 'LIVEIN_EVENT') {
        return sum + 8;
      }
      if (event.breaks) {
        breakDuration = (event.breaks.breakDuration * event.breaks.breakCount) / 60;
      }
      const duration = moment.duration(endTime.diff(startTime));
      return sum + duration.asHours() - breakDuration;
    }, 0);
    return totalHours;
  }, [rangeView, view, events]);

  return (
    <Box
      sx={{
        margin: '0 auto',
        boxShadow: 3,
        borderRadius: 2,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center'}}>
        {/* Calendar/summary icon */}
        <AssignmentIcon sx={{fontSize: 24, marginRight: 1}} />
        <Translate
          expression='client.ClientDetail.Scheduling.Summary.SummaryTitle'
          style={{fontWeight: 'bold', fontSize: 20}}
        />
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center'}}>
        {/* Clock icon */}
        <AccessTimeIcon sx={{fontSize: 20, marginRight: 1, color: '#1976d2'}} />
        <Translate
          expression='client.ClientDetail.Scheduling.Summary.Hours'
          substitute={{hours: numberOfHours}}
          style={{fontWeight: '500', color: '#1976d2'}}
        />
      </Box>
      {
        hourlyRate != null && (
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            {/* Money icon */}
            <AttachMoneyIcon sx={{fontSize: 20, marginRight: 1, color: '#388e3c'}} />
            <Translate
              expression='client.ClientDetail.Scheduling.Summary.HourlyRate'
              substitute={{cost: hourlyRate}}
              style={{fontWeight: '500', color: '#388e3c'}}
            />
          </Box>
        )
      }
      {
        hourlyRate != null && typeof hourlyRate === 'number' && !isNaN(hourlyRate) && (
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            {/* Total cost icon */}
            <ReceiptIcon sx={{fontSize: 20, marginRight: 1, color: '#f57c00'}} />
            <Translate
              expression='client.ClientDetail.Scheduling.Summary.Cost'
              substitute={{cost: numberOfHours * hourlyRate}}
              style={{fontWeight: 'bold', color: '#f57c00'}}
            />
          </Box>
        )
      }
    </Box>
  );
};

ScheduleCost.propTypes = {
  rangeView: PropTypes.any,
  view: PropTypes.string.isRequired,
  events: PropTypes.object.isRequired,
  clientPaymentPlan: PropTypes.object,
};
export default React.memo(ScheduleCost);
